import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
  Box,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import ProjectViewController from "../../redux/actions/ProjectApiAction";
import TaskVeiwController from "../../redux/actions/TaskApiAction";
import EmployerViewController from "../../redux/actions/EmployerApiAction";
import { IMAGE_URL } from "../../services/api";
import { getUserInfoFromToken } from "../../utils/authUtils";
import ProtectedImage from "../../components/ProtectedImage";
import FormDrawer from "../../components/layout/FormDrawer";
import MobileSelectField from "../../components/mobile/MobileSelectField";
import { BRAND, primaryButtonSx, inputSx, mobileInputSx } from "../../components/auth/authTheme";

import "../../styles/styles.css";
import "../../styles/layout.css";

const CATEGORY_OPTIONS = [
  { value: "Task", label: "Task" },
  { value: "Issue", label: "Issue Tracker" },
];

const PRIORITY_OPTIONS = [
  { value: "Low", label: "Low" },
  { value: "Medium", label: "Medium" },
  { value: "High", label: "High" },
  { value: "Critical", label: "Critical" },
];

const STATUS_OPTIONS = [
  { value: "New", label: "New" },
  { value: "Pending", label: "Pending" },
  { value: "In Progress", label: "In Progress" },
  { value: "Ready for Testing", label: "Ready for Testing" },
  { value: "Testing", label: "Testing" },
  { value: "Changes Required", label: "Changes Required" },
  { value: "Reopened", label: "Reopened" },
  { value: "Completed", label: "Completed" },
  { value: "Blocked", label: "Blocked" },
];

const SectionCard = ({ title, isMobile, children, mb = 1.5 }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: isMobile ? "12px" : 0,
      border: isMobile ? `1px solid ${BRAND.border}` : "none",
      p: isMobile ? 1.5 : 0,
      mb: isMobile ? mb : 3,
    }}
  >
    {title && (
      <Typography
        variant="subtitle2"
        sx={{ color: BRAND.navy, fontWeight: 700, mb: 1.5 }}
      >
        {title}
      </Typography>
    )}
    {children}
  </Box>
);

const AddTaskFormModal = ({
  show,
  handleClose,
  initialData = null,
  selectedProject,
  category = "Task",
  onSave,
}) => {
  const [userType, setUserType] = useState(null);
  const [userId, setUserId] = useState(null);

  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fieldSx = isMobile ? mobileInputSx : inputSx;
  const gridSpacing = isMobile ? 1.5 : 2.5;

  const { addTask, updateTask } = TaskVeiwController();
  const { getProjects } = ProjectViewController();
  const { getAllEmployeeEmployer } = EmployerViewController();
  const { employers } = useSelector((state) => state.employer);
  const { projects } = useSelector((state) => state.project);
  const { isLoding } = useSelector((state) => state.task);

  const isEdit = Boolean(initialData?.id);

  const initialValues = {
    project: initialData?.project?.id || selectedProject?.id || "",
    title: initialData?.title || "",
    description: initialData?.description || "",
    assigned_to: initialData?.assigned_to?.id || "",
    status: initialData?.status || "",
    priority: initialData?.priority || "",
    category: initialData?.category || category || "",
    start_date: initialData?.start_date || "",
    end_date: initialData?.end_date || "",
    issue_logged_by_employer: initialData?.issue_logged_by_employer || "",
    issue_logged_by_employee: initialData?.issue_logged_by_employee || "",
    attachments: [],
  };

  const projectTitle =
    initialData?.project?.title ||
    selectedProject?.title ||
    "No project selected";

  const existingAttachments = initialData?.attachments || [];

  const formatEmpLabel = (emp) => {
    const name = `${emp.first_name || ""} ${emp.last_name || ""}`.trim();
    if (emp.employee_id) return `${name} (${emp.employee_id})`;
    return `${name} (Employer)`;
  };

  const employerOptions = (employers || []).map((emp) => ({
    label: formatEmpLabel(emp),
    value: emp.id,
  }));

  const projectOptions = (projects || []).map((p) => ({
    label: p.title,
    value: p.id,
  }));

  useEffect(() => {
    if (show) {
      if (!employers?.length) dispatch(getAllEmployeeEmployer());
      if (!projects?.length) dispatch(getProjects());
    }
  }, [show, dispatch, employers, projects]);

  useEffect(() => {
    const { userType: type, userId: id } = getUserInfoFromToken();
    setUserType(type);
    setUserId(id);
  }, []);

  const validationSchema = Yup.object().shape({
    category: Yup.string().required("Category is required"),
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    assigned_to: Yup.string().required("Assigned To is required"),
    priority: Yup.string().required("Priority is required"),
    status: Yup.string().required("Status is required"),
    start_date: Yup.string().required("Start Date is required"),
    end_date: Yup.string().required("End Date is required"),
  });

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    const formData = new FormData();
    values.issue_logged_by_employer = userType === "employer" ? userId : "";
    values.issue_logged_by_employee = userType === "employee" ? userId : "";

    Object.entries(values).forEach(([key, value]) => {
      if (key === "attachments") {
        for (const file of value) formData.append("attachments", file);
      } else {
        formData.append(key, value);
      }
    });

    try {
      const action = isEdit
        ? updateTask(initialData.id, formData)
        : addTask(formData);
      await dispatch(action);
      resetForm();
      onSave?.(values.project, values.category);
      handleClose();
    } catch (err) {
      console.error("Submit error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const drawerTitle = isEdit
    ? initialData?.category === "Issue"
      ? "Edit Issue"
      : "Edit Task"
    : category === "Issue"
      ? "Add Issue"
      : "Add Task";

  const renderFooter = (isSaving, submitForm) =>
    isMobile ? (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
        <Button
          type="button"
          variant="contained"
          color="primary"
          disabled={isSaving}
          onClick={() => submitForm()}
          fullWidth
          sx={{
            ...primaryButtonSx,
            minHeight: 48,
            borderRadius: "12px",
            "&.Mui-disabled": {
              bgcolor: isSaving ? BRAND.orange : undefined,
              color: isSaving ? "#fff" : undefined,
              opacity: isSaving ? 0.85 : undefined,
            },
          }}
          startIcon={
            isSaving ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {isSaving
            ? isEdit
              ? "Updating..."
              : "Creating..."
            : isEdit
              ? "Update"
              : "Create"}
        </Button>
        <Button
          onClick={handleClose}
          color="inherit"
          disabled={isSaving}
          fullWidth
          sx={{ minHeight: 44, textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
      </Box>
    ) : (
      <>
        <Button onClick={handleClose} color="inherit" disabled={isSaving}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          disabled={isSaving}
          onClick={() => submitForm()}
          sx={{
            ...primaryButtonSx,
            minWidth: 120,
            "&.Mui-disabled": {
              bgcolor: isSaving ? BRAND.orange : undefined,
              color: isSaving ? "#fff" : undefined,
              opacity: isSaving ? 0.85 : undefined,
            },
          }}
          startIcon={
            isSaving ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {isSaving
            ? isEdit
              ? "Updating..."
              : "Creating..."
            : isEdit
              ? "Update"
              : "Create"}
        </Button>
      </>
    );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnBlur
      validateOnChange={false}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        handleChange,
        handleBlur,
        isSubmitting,
        submitForm,
      }) => {
        const isSaving = isSubmitting || isLoding;

        return (
          <Form style={{ display: "contents" }}>
            <FormDrawer
              open={show}
              onClose={handleClose}
              title={drawerTitle}
              subtitle={`Project: ${projectTitle}`}
              width={{ xs: "100%", sm: 520, md: 560 }}
              footer={renderFooter(isSaving, submitForm)}
            >
              <SectionCard title="Task details" isMobile={isMobile}>
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12} md={6}>
                    {isMobile ? (
                      <MobileSelectField
                        required
                        label="Category"
                        value={values.category}
                        onChange={(val) => setFieldValue("category", val)}
                        options={CATEGORY_OPTIONS}
                        placeholder="Select category"
                        helperText={touched.category && errors.category}
                      />
                    ) : (
                      <Autocomplete
                        fullWidth
                        options={CATEGORY_OPTIONS}
                        getOptionLabel={(option) => option.label}
                        value={
                          CATEGORY_OPTIONS.find(
                            (opt) => opt.value === values.category
                          ) || null
                        }
                        onChange={(e, newValue) =>
                          setFieldValue("category", newValue?.value || "")
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Category"
                            required
                            size="small"
                            error={touched.category && Boolean(errors.category)}
                            helperText={touched.category && errors.category}
                            InputProps={{ ...params.InputProps, sx: { height: 48 } }}
                          />
                        )}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {isMobile ? (
                      <MobileSelectField
                        label="Project"
                        value={values.project}
                        onChange={(val) => setFieldValue("project", val)}
                        options={projectOptions}
                        placeholder={projectTitle}
                        disabled
                      />
                    ) : (
                      <TextField
                        fullWidth
                        label="Project"
                        size="small"
                        value={projectTitle}
                        disabled
                        InputProps={{ sx: { height: 48 } }}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      name="title"
                      label="Title"
                      required
                      value={values.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      size="small"
                      error={touched.title && Boolean(errors.title)}
                      helperText={touched.title && errors.title}
                      sx={fieldSx}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      name="description"
                      label="Description"
                      required
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      multiline
                      rows={isMobile ? 3 : 3}
                      fullWidth
                      size="small"
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard title="Assignment" isMobile={isMobile}>
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12}>
                    {isMobile ? (
                      <MobileSelectField
                        required
                        label="Assigned To"
                        value={values.assigned_to}
                        onChange={(val) => setFieldValue("assigned_to", val)}
                        options={employerOptions}
                        placeholder="Select assignee"
                        helperText={touched.assigned_to && errors.assigned_to}
                      />
                    ) : (
                      <Autocomplete
                        fullWidth
                        options={employers || []}
                        getOptionLabel={(emp) => formatEmpLabel(emp)}
                        value={
                          (employers || []).find((e) => e.id === values.assigned_to) ||
                          null
                        }
                        onChange={(event, newValue) =>
                          setFieldValue("assigned_to", newValue ? newValue.id : "")
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Assigned To"
                            required
                            size="small"
                            error={touched.assigned_to && Boolean(errors.assigned_to)}
                            helperText={touched.assigned_to && errors.assigned_to}
                            InputProps={{ ...params.InputProps, sx: { height: 48 } }}
                          />
                        )}
                      />
                    )}
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard title="Priority & status" isMobile={isMobile}>
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12} md={6}>
                    {isMobile ? (
                      <MobileSelectField
                        required
                        label="Priority"
                        value={values.priority}
                        onChange={(val) => setFieldValue("priority", val)}
                        options={PRIORITY_OPTIONS}
                        placeholder="Select priority"
                        helperText={touched.priority && errors.priority}
                      />
                    ) : (
                      <Autocomplete
                        fullWidth
                        options={PRIORITY_OPTIONS}
                        getOptionLabel={(opt) => opt.label}
                        value={
                          PRIORITY_OPTIONS.find(
                            (opt) => opt.value === values.priority
                          ) || null
                        }
                        onChange={(e, newValue) =>
                          setFieldValue("priority", newValue?.value || "")
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Priority"
                            required
                            size="small"
                            error={touched.priority && Boolean(errors.priority)}
                            helperText={touched.priority && errors.priority}
                            InputProps={{ ...params.InputProps, sx: { height: 48 } }}
                          />
                        )}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {isMobile ? (
                      <MobileSelectField
                        required
                        label="Status"
                        value={values.status}
                        onChange={(val) => setFieldValue("status", val)}
                        options={STATUS_OPTIONS}
                        placeholder="Select status"
                        helperText={touched.status && errors.status}
                      />
                    ) : (
                      <Autocomplete
                        fullWidth
                        options={STATUS_OPTIONS}
                        getOptionLabel={(opt) => opt.label}
                        value={
                          STATUS_OPTIONS.find((opt) => opt.value === values.status) ||
                          null
                        }
                        onChange={(e, newValue) =>
                          setFieldValue("status", newValue?.value || "")
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Status"
                            required
                            size="small"
                            error={touched.status && Boolean(errors.status)}
                            helperText={touched.status && errors.status}
                            InputProps={{ ...params.InputProps, sx: { height: 48 } }}
                          />
                        )}
                      />
                    )}
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard title="Timeline" isMobile={isMobile}>
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="date"
                      name="start_date"
                      label="Start Date"
                      required
                      value={values.start_date}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      error={touched.start_date && Boolean(errors.start_date)}
                      helperText={touched.start_date && errors.start_date}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      type="date"
                      name="end_date"
                      label="End Date"
                      required
                      value={values.end_date}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      error={touched.end_date && Boolean(errors.end_date)}
                      helperText={touched.end_date && errors.end_date}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard title="Attachments" isMobile={isMobile} mb={0}>
                {isEdit && existingAttachments.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography gutterBottom sx={{ color: BRAND.navy, fontWeight: 600 }}>
                      Existing Attachments
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {existingAttachments.map((url, index) => {
                        const fullUrl = `${IMAGE_URL}${url}`;
                        const isPDF = fullUrl.toLowerCase().endsWith(".pdf");
                        const isImage = /\.(jpg|jpeg|png|gif)$/i.test(fullUrl);

                        return isImage ? (
                          <ProtectedImage
                            key={index}
                            path={fullUrl}
                            alt={`Attachment ${index + 1}`}
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              cursor: "pointer",
                              border: "1px solid #ccc",
                            }}
                            onClick={() => window.open(fullUrl, "_blank")}
                          />
                        ) : (
                          <Button
                            key={index}
                            onClick={() => window.open(fullUrl, "_blank")}
                            size="small"
                          >
                            {isPDF ? `View PDF ${index + 1}` : `Download File ${index + 1}`}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  size="small"
                  sx={{
                    borderColor: BRAND.orange,
                    color: BRAND.orange,
                    textTransform: "none",
                    borderRadius: "10px",
                  }}
                >
                  Upload Files
                  <input
                    name="attachments"
                    type="file"
                    multiple
                    hidden
                    onChange={(event) => {
                      setFieldValue("attachments", event.currentTarget.files);
                    }}
                  />
                </Button>

                {values.attachments?.length > 0 && (
                  <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                    {Array.from(values.attachments).map((file, index) => (
                      <Typography key={index} sx={{ fontSize: "0.875rem", color: BRAND.navy }}>
                        {file.name}
                      </Typography>
                    ))}
                  </Box>
                )}
              </SectionCard>
            </FormDrawer>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AddTaskFormModal;
